import React, {
    type Dispatch,
    type SetStateAction,

    useEffect,
    useMemo,
} from 'react';
import {useDispatch} from 'react-redux';

import clsx from 'clsx';
import {SelectionRow, clearSelection} from 'src/components/data-table-selection';
import {ToolsRibbonComponent} from 'src/components/tools-ribbon';
import {NO_RESULTS_MESSAGE} from 'src/constants';

import {
    Cell,
    type CellOptions,
    type Column,
    DataTable,

    type DataTableProps,
    type HeaderCellOptions,
    type RowsParams,
    type StopResizeParams,
    TableHeader,
} from '@pb/kit-component-data-table-v2';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {StickyElement} from '@pb/kit-component-sticky-element';

import {
    HighlightedText,
    MainColumnAssignedResourceCell,
    MainColumnFreeResourceCell,
    MainColumnGroupCell,
    MainColumnHeader,
} from './components/main-column';
import {mainColumnCellGroupRow} from './components/main-column/main-column-cell.module.styl';
import {
    COLUMNS,
    COLUMNS_DATA,
    HEADERS,
} from './constants';

import type {ResourceView} from 'src/modules/entitlements/typedef';

type PaginationComponent = DataTableProps['paginationStripe'] | null;

const renderLink = (content: React.ReactNode, entityId?: string, onOpen?: (entityId: string) => void) => (
    onOpen && entityId
        ? <a onClick={() => onOpen(entityId)}>{content}</a>
        : content
);

type Props = {
    dataTableId: string,
    selectionKey: string,
    rows: ResourceView[],
    shortHeader: React.ReactElement,
    resourceFilter: string,

    expandedRoleIds: string[],
    setExpandedRoleIds: Dispatch<SetStateAction<string[]>>,

    toolRibbonClassName?: string,
    customOffset: number,
    readonly?: boolean,
    paginationStripe?: PaginationComponent,
    children?: (ids: Array<number | string>) => React.ReactNode,
    filtersTools?: React.ReactNode,

    onOpenResource?: (resourceId: string) => void,
    onOpenGrant?: (grantId: string) => void,
    onOpenRole?: (roleId: string) => void,

    getColumnWidthByColumnId: (columnId: string) => number,
    onStopResize: (params: StopResizeParams) => void,
};

export const PreparedEntitlementsView = (props: Props) => {
    const {
        rows,
        selectionKey,
        shortHeader,
        resourceFilter,
        toolRibbonClassName,
        readonly,
        customOffset,
        expandedRoleIds,
        setExpandedRoleIds,
        children,
        getColumnWidthByColumnId,
        onStopResize,
        dataTableId,
        paginationStripe,
        filtersTools,
        onOpenResource,
        onOpenGrant,
        onOpenRole,
    } = props;

    const dispatch = useDispatch();

    const renderedRows = useMemo(() => (rows
        .filter(entitlement => {
            const record = JSON.parse(entitlement.recordId);
            return (record.roleId && record.resourceId
                ? expandedRoleIds.includes(record.roleId)
                : true
            );
        })
        .map(entitlement => ({
            id: entitlement.recordId,
            columnValues: {
                title: entitlement.title,
                description: entitlement.description,
                grant: entitlement.grantTitle,
                status: entitlement.statusTitle,
                restriction: entitlement.restrictionTitle,
            },
        }))
    ), [rows, expandedRoleIds]);

    const selectableRows = useMemo(() => (renderedRows
        .filter(row => {
            const record = JSON.parse(row.id);
            return record.roleId ? !record.resourceId : true;
        })
        .map(({id}) => id) || []
    ), [renderedRows]);

    useEffect(() => {
        return () => {
            dispatch(clearSelection(selectionKey));
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div>
            {!readonly && (
                <StickyElement className={`sticky-tool-ribbon ${toolRibbonClassName || ''}`}>

                    <ToolsRibbonComponent {...{
                        rows: selectableRows,
                        selectionKey,
                        showSelectAll: false,
                        totalCount: selectableRows.length,
                        children,
                        filtersTools,
                    }}
                    />
                </StickyElement>
            )}
            <DataTable
                id={dataTableId}
                data={{
                    rows: renderedRows, columns: COLUMNS_DATA.map((column: Column) => ({
                        ...column,
                        width: getColumnWidthByColumnId(column.columnId),
                    })),
                }}
                getHeaderTopOffset={() => (readonly
                    ? customOffset - 40
                    : customOffset
                )}
                onStopResize={onStopResize}
                headers={({column}: {column: Column}, cellOptions: HeaderCellOptions) => {
                    switch (column.columnId) {
                        case COLUMNS.TITLE:
                            return (
                                <Cell
                                    {...cellOptions}
                                    key={`${column.columnId}`}
                                    column={column}
                                    resizable={true}>
                                    <MainColumnHeader selection={<div/>}>
                                        {shortHeader}
                                    </MainColumnHeader>
                                </Cell>
                            );
                        default:
                            return (
                                <Cell
                                    {...cellOptions}
                                    key={`${column.columnId}`}
                                    column={column}
                                    resizable={true}>
                                    <TableHeader indent="none" className="search-header">
                                        <strong>{HEADERS[column.columnId]}</strong>
                                    </TableHeader>
                                </Cell>
                            );
                    }
                }}
                renderRows={(rowsParams: RowsParams, cellOptions: CellOptions) => {
                    const {rowIndex, column: {columnId}} = rowsParams;
                    const value = rowsParams.row.columnValues[columnId] as string;
                    const column = rowsParams.column;
                    const rowId = rowsParams.row.id;
                    const cellKey = `${rowId}-${columnId}`;

                    const record = JSON.parse(rowId as string);

                    const isGroupRow = !!record.roleId && !record.resourceId;
                    const roleId = isGroupRow ? record.roleId : undefined;
                    const isAssignedResource = !!(record.roleId && record.resourceId);

                    const cellClasses = clsx('search-result-cell', [
                        isGroupRow && mainColumnCellGroupRow,
                    ]);

                    switch (columnId) {
                        case COLUMNS.TITLE:
                            if (isGroupRow) {
                                return (
                                    <MainColumnGroupCell
                                        key={cellKey}
                                        {...{
                                            selection: !readonly && (
                                                <SelectionRow
                                                    rows={selectableRows}
                                                    index={rowIndex}
                                                    entityId={rowId}
                                                    selectionKey={selectionKey}
                                                    totalCount={selectableRows.length}
                                                />
                                            ),
                                            isExpanded: expandedRoleIds.includes(roleId as string),
                                            onExpand: () => setExpandedRoleIds(
                                                prev => [...prev, roleId as string],
                                            ),
                                            onCollapse: () => setExpandedRoleIds(
                                                prev => prev.filter(id => id !== roleId),
                                            ),
                                            cellOptions: {...cellOptions, column},
                                        }}>
                                        <EllipsisTextHint title={value}>
                                            {renderLink(<strong>{value}</strong>, record.roleId, onOpenRole)}
                                        </EllipsisTextHint>
                                    </MainColumnGroupCell>
                                );
                            }

                            if (isAssignedResource) {
                                return (
                                    <MainColumnAssignedResourceCell
                                        key={cellKey}
                                        {...{cellOptions: {...cellOptions, column}}}>
                                        <EllipsisTextHint title={value}>
                                            {renderLink(
                                                <HighlightedText text={value} highlightedText={resourceFilter}/>,
                                                record.resourceId,
                                                onOpenResource,
                                            )}
                                        </EllipsisTextHint>
                                    </MainColumnAssignedResourceCell>
                                );
                            }

                            return (
                                <MainColumnFreeResourceCell
                                    key={cellKey}
                                    {...{
                                        selection: !readonly && (
                                            <SelectionRow
                                                rows={selectableRows}
                                                index={rowIndex}
                                                entityId={rowId}
                                                selectionKey={selectionKey}
                                                totalCount={selectableRows.length}
                                            />
                                        ),
                                        cellOptions: {...cellOptions, column},
                                        groupRow: isGroupRow,
                                    }}>
                                    <EllipsisTextHint title={value}>
                                        <div className={isAssignedResource ? 'pl-l' : undefined}>
                                            {renderLink(
                                                <HighlightedText text={value} highlightedText={resourceFilter}/>,
                                                record.resourceId,
                                                onOpenResource,
                                            )}
                                        </div>
                                    </EllipsisTextHint>
                                </MainColumnFreeResourceCell>
                            );
                        case COLUMNS.GRANT:
                            return (
                                <Cell
                                    {...cellOptions}
                                    key={cellKey}
                                    column={column}
                                    className={cellClasses}>
                                    <EllipsisTextHint title={value}>
                                        {renderLink(value, record.grantId, onOpenGrant)}
                                    </EllipsisTextHint>
                                </Cell>
                            );
                        default:
                            return (
                                <Cell
                                    {...cellOptions}
                                    key={cellKey}
                                    column={column}
                                    className={cellClasses}>
                                    <EllipsisTextHint title={value}>
                                        {value}
                                    </EllipsisTextHint>
                                </Cell>
                            );
                    }
                }}
                showNoDataMsg={!selectableRows.length}
                tableMessage={NO_RESULTS_MESSAGE}
                paginationStripe={paginationStripe || undefined}
            />
        </div>
    );
};
